import {
	createTheme,
	darken,
	lighten,
} from '@mui/material/styles';
// Import theme config.
import ThemeConfig from '@config/theme';

const onButtonKeyPress: (event: React.KeyboardEvent<HTMLButtonElement>) => void = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
	if (event.key === 'Enter') {
		event.stopPropagation();
	}
}

const onGroupInputKeyPress: (event: React.KeyboardEvent<HTMLButtonElement>) => void = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
	if (event.key === 'Enter') {
		event.preventDefault();
	}
}

const theme = createTheme({
	zIndex: {
		tooltip: ThemeConfig.level_1,
		modal: ThemeConfig.level_1,
		appBar: ThemeConfig.level_3,
		drawer: ThemeConfig.level_3,
		mobileStepper: ThemeConfig.level_5,
	},
	components: {
		MuiSwitch: {
			defaultProps: {
				onKeyPress: onGroupInputKeyPress,
			}
		},
		MuiFab: {
			defaultProps: {
				onKeyPress: onButtonKeyPress,
			},
		},
		MuiListItemText: {
			styleOverrides: {
				root: {
					color: ThemeConfig.infoColor,
				}
			}
		},
		MuiListItemIcon: {
			styleOverrides: {
				root: {
					color: ThemeConfig.infoColor,
				}
			}
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					whiteSpace: 'normal',
				},
			}
		},
		MuiFormControlLabel: {
			styleOverrides: {
				root: {
					color: ThemeConfig.infoColor,
				}
			}
		},
		MuiCheckbox: {
			defaultProps: {
				onKeyPress: onGroupInputKeyPress,
			},
			styleOverrides: {
				root: {
					
				}
			}
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					
				}
			}
		},
		MuiButtonBase: {
			defaultProps: {
				onKeyPress: onButtonKeyPress,
			},
		},
		MuiButton: {
			defaultProps: {
				onKeyPress: onButtonKeyPress,
			},
			styleOverrides: {
				root: ThemeConfig.useSquareTheme ? {
					borderRadius: 0,
					padding: '0px',
					lineHeight: 'normal',
				} : {
					lineHeight: 'normal',
				},
				text: ThemeConfig.useSquareTheme ? {
					padding: '0px',
				} : {

				},
				startIcon: ThemeConfig.useSquareTheme ? {
					borderRightWidth: '1px',
					borderRightStyle: 'solid',
					borderRightColor: ThemeConfig.buttonDividerColor,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					marginLeft: '0px',
					paddingRight: '8px',
				} : {
					marginLeft: '0px',
				},
				endIcon: ThemeConfig.useSquareTheme ? {
					borderLeftWidth: '1px',
					borderLeftStyle: 'solid',
					borderLeftColor: ThemeConfig.buttonDividerColor,
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					marginRight: '0px',
					paddingLeft: '8px',
				} : {
					marginRight: '0px'
				},
				textPrimary: ThemeConfig.useSquareTheme ? {
					height: '100%',
					padding: '0px 8px',
				} : {

				},
			}
		},
		MuiIconButton: {
			defaultProps: {
				onKeyPress: onButtonKeyPress,
			},
			styleOverrides: {
				root: {
					padding: '8px',
				}
			}
		},
		MuiInput: {
			styleOverrides: {
				root: {
					borderBottomColor: ThemeConfig.infoColor,
				}
			}
		},
		MuiOutlinedInput: {
			styleOverrides: {
				notchedOutline: {
					borderColor: ThemeConfig.infoColor,
				}
			}
		},
		MuiInputLabel: {
			styleOverrides: {
				root: {
					color: ThemeConfig.infoColor,
				},
				outlined: {
					WebkitTransform: 'none',
					msTransform: 'none',
					transform: 'none',
					MozTransform: 'none',
					position: 'initial',
					marginBottom: '16px',
					whiteSpace: 'initial',
				},
			}
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					color: ThemeConfig.infoColor,
					marginLeft: 0,
				}
			}
		},
		MuiFormLabel: {
			styleOverrides: {
				root: {
					color: ThemeConfig.infoColor,
				}
			}
		},
		MuiDivider: {
			styleOverrides: {
				root: {
					borderColor: ThemeConfig.dividerColor
				}
			}
		},
	},
	shape: {
		borderRadius: ThemeConfig.useSquareTheme ? 0 : 4,
	},
	palette: {
		background: {
			default: ThemeConfig.backgroundColor,
		},
		primary: {
			main: ThemeConfig.primaryColor,
			contrastText: ThemeConfig.primaryContrastTextColor,
			"100": lighten(ThemeConfig.primaryColor, .95),
			"200": lighten(ThemeConfig.primaryColor, .85),
			"300": lighten(ThemeConfig.primaryColor, .65),
			"400": lighten(ThemeConfig.primaryColor, .50),
			"500": lighten(ThemeConfig.primaryColor, .35),
			"600": lighten(ThemeConfig.primaryColor, .20),
			"700": ThemeConfig.primaryColor,
			"800": darken(ThemeConfig.primaryColor, .15),
			"900": darken(ThemeConfig.primaryColor, .30),
		},
		secondary: {
			main: ThemeConfig.secondaryColor,
			contrastText: ThemeConfig.secondaryContrastTextColor,
			"100": lighten(ThemeConfig.secondaryColor, .95),
			"200": lighten(ThemeConfig.secondaryColor, .85),
			"300": lighten(ThemeConfig.secondaryColor, .65),
			"400": lighten(ThemeConfig.secondaryColor, .50),
			"500": lighten(ThemeConfig.secondaryColor, .35),
			"600": lighten(ThemeConfig.secondaryColor, .20),
			"700": ThemeConfig.secondaryColor,
			"800": darken(ThemeConfig.secondaryColor, .15),
			"900": darken(ThemeConfig.secondaryColor, .30),
		},
		warning: {
			main: ThemeConfig.warningColor,
			contrastText: ThemeConfig.warningContrastTextColor,
			"100": lighten(ThemeConfig.warningColor, .95),
			"200": lighten(ThemeConfig.warningColor, .85),
			"300": lighten(ThemeConfig.warningColor, .65),
			"400": lighten(ThemeConfig.warningColor, .50),
			"500": lighten(ThemeConfig.warningColor, .35),
			"600": lighten(ThemeConfig.warningColor, .20),
			"700": ThemeConfig.warningColor,
			"800": darken(ThemeConfig.warningColor, .15),
			"900": darken(ThemeConfig.warningColor, .30),
		},
		error: {
			main: ThemeConfig.errorColor,
			contrastText: ThemeConfig.errorContrastTextColor,
			"100": lighten(ThemeConfig.errorColor, .95),
			"200": lighten(ThemeConfig.errorColor, .85),
			"300": lighten(ThemeConfig.errorColor, .65),
			"400": lighten(ThemeConfig.errorColor, .50),
			"500": lighten(ThemeConfig.errorColor, .35),
			"600": lighten(ThemeConfig.errorColor, .20),
			"700": ThemeConfig.errorColor,
			"800": darken(ThemeConfig.errorColor, .15),
			"900": darken(ThemeConfig.errorColor, .30),
		},
		info: {
			main: ThemeConfig.infoColor,
			contrastText: ThemeConfig.infoContrastTextColor,
			"100": lighten(ThemeConfig.infoColor, .95),
			"200": lighten(ThemeConfig.infoColor, .85),
			"300": lighten(ThemeConfig.infoColor, .65),
			"400": lighten(ThemeConfig.infoColor, .50),
			"500": lighten(ThemeConfig.infoColor, .35),
			"600": lighten(ThemeConfig.infoColor, .20),
			"700": ThemeConfig.infoColor,
			"800": darken(ThemeConfig.infoColor, .15),
			"900": darken(ThemeConfig.infoColor, .30),
		},
		success: {
			main: ThemeConfig.successColor,
			contrastText: ThemeConfig.successContrastTextColor,
			"100": lighten(ThemeConfig.successColor, .95),
			"200": lighten(ThemeConfig.successColor, .85),
			"300": lighten(ThemeConfig.successColor, .65),
			"400": lighten(ThemeConfig.successColor, .50),
			"500": lighten(ThemeConfig.successColor, .35),
			"600": lighten(ThemeConfig.successColor, .20),
			"700": ThemeConfig.successColor,
			"800": darken(ThemeConfig.successColor, .15),
			"900": darken(ThemeConfig.successColor, .30),
		}
	},
});

export default theme;
